import { z } from 'zod';

import { isType } from '@ballerine/common';

export type CompanySanctionMatch = {
  id?: string;
  primaryName?: string;
  lastReviewed?: string;
  labels?: string[];
  reasonsForMatch?: string[];
  sources?: Array<{ url: string; categories?: string[] }>;
  fullReport?: Record<string, unknown>;
  alternativeNames?: Array<{ name: string; type?: string }> | string[];
  officialLists?: Array<{ description: string }>;
  furtherInformation?: string[];
  linkedIndividuals?: Array<{
    firstName?: string;
    middleName?: string;
    lastName?: string;
    description?: string;
  }>;
  places?: Array<{
    country?: string;
    city?: string;
    address?: string;
  }>;
};

const TAG_DEFINITIONS = [
  {
    tag: 'Sanctions',
    matchers: (sanction: string) => sanction.toLowerCase().includes('sanctions'),
  },
  {
    tag: 'Adverse Media',
    matchers: (sanction: string) => sanction.toLowerCase().includes('reputational risk exposure'),
  },
  {
    tag: 'PEP',
    matchers: (sanction: string) => sanction.toLowerCase().includes('pep'),
  },
  {
    tag: 'State-Owned Enterprise',
    matchers: (sanction: string) =>
      sanction.toLowerCase().includes('soe') || sanction.toLowerCase().includes('state owned'),
  },
  {
    tag: 'Terrorism',
    matchers: (sanction: string) => sanction.toLowerCase().includes('terror'),
  },
] as const satisfies Array<{
  tag: string;
  matchers: (sanction: string) => boolean;
}>;

const collectCategoryStrings = (match: CompanySanctionMatch): string[] => {
  const out: string[] = [];
  const checkIsFullReportWithEntityCategory = isType(
    z.object({
      fullReport: z.object({
        entity: z.object({
          category: z.string(),
        }),
      }),
    }),
  );
  const isFullReportWithEntityCategory = checkIsFullReportWithEntityCategory(match);

  out.push(...(match.labels ?? []));

  if (isFullReportWithEntityCategory) {
    out.push((match.fullReport as { entity: { category: string } }).entity.category);
  }

  const sourceCategories = match.sources?.flatMap(source => source.categories ?? []) ?? [];

  out.push(...sourceCategories);

  return out;
};

export const extractSanctionTags = (match: CompanySanctionMatch): string[] => {
  const strings = collectCategoryStrings(match);
  const seen = new Set<string>();
  const result: string[] = [];

  for (const { tag, matchers } of TAG_DEFINITIONS) {
    if (seen.has(tag)) {
      continue;
    }

    const matched = strings.some(
      (string_: string) => typeof string_ === 'string' && matchers(string_),
    );

    if (!matched) {
      continue;
    }

    seen.add(tag);
    result.push(tag);
  }

  return result;
};
